import { ImportImage } from "@pdfking/compatibility"
import {
  type PdfiumApi,
  PdfiumExports as E,
  type PdfiumTypeMap,
  type Pointer,
} from "@pdfking/pdfium-wrap"
import type { Bytes } from "@pdfking/tfunc"
import {
  BlobToUint8,
  Items,
  IterableToArray,
  SafePromise,
  ToBlob,
} from "@pdfking/tfunc"

import { CloseDocument } from "../functions/CloseDocument"
import { ReadDocument } from "../functions/ReadDocument"
import { SaveDocument } from "../functions/SaveDocument"
import { ProvisionPdfium } from "../utils/ProvisionPdfium"

type PointerDoc = PdfiumTypeMap["FPDF_DOCUMENT"]
type PointerImageObj = PdfiumTypeMap["FPDF_PAGEOBJECT"]

type AttemptFunction = (
  api: PdfiumApi,
  blob: Blob,
  resolution: number
) => AsyncGenerator<Blob>

type ImageLoader = (
  api: PdfiumApi,
  doc: PointerDoc,
  imageObj: PointerImageObj,
  blob: Blob
) => Promise<{ cleanup: () => Promise<void> }>

// Load existing PDF
async function* loadPdf(api: PdfiumApi, blob: Blob): AsyncGenerator<Blob> {
  const reader = await ReadDocument(api, {
    stream: [blob],
    size: blob.size as Bytes,
  })

  try {
    yield* SaveDocument(api, reader.doc_pointer)
  } finally {
    await CloseDocument(api, reader)
  }
}

// JPEG loader using FPDFImageObj_LoadJpegFileInline
const loadJpeg: ImageLoader = async (api, _doc, imageObj, blob) => {
  const { call, malloc, writeBytes, setInt32 } = api

  const bytes = await BlobToUint8(blob)
  const { free: freeData } = writeBytes(bytes)
  const { pointer: fileAccessPtr, free: freeFileAccess } = malloc(12 as Bytes)

  const getBlock = (
    _param: number,
    position: number,
    pBuf: number,
    size: number
  ) => {
    api.writeTo(pBuf as Pointer, bytes.subarray(position, position + size))
    return 1
  }

  const { pointer: getBlockPtr, free: freeGetBlock } = api.addCallback(
    getBlock,
    "iiiii"
  )

  setInt32(fileAccessPtr, bytes.length)
  setInt32((fileAccessPtr + 4) as Pointer, getBlockPtr)
  setInt32((fileAccessPtr + 8) as Pointer, 0 as unknown as Pointer)

  const success = await call(
    E.FPDFImageObj_LoadJpegFileInline,
    0 as unknown as Pointer,
    0,
    imageObj,
    fileAccessPtr
  )

  if (!success) {
    freeData()
    freeFileAccess()
    freeGetBlock()
    throw new Error("Failed to load JPEG into image object")
  }

  return {
    cleanup: async () => {
      freeData()
      freeFileAccess()
      freeGetBlock()
    },
  }
}

// Convert RGBA to BGRA (swap R and B channels)
const rgbaToBgra = (data: Uint8Array): Uint8Array => {
  const result = new Uint8Array(data.length)
  for (let i = 0; i < data.length; i += 4) {
    result[i] = data[i + 2] // B <- R
    result[i + 1] = data[i + 1] // G
    result[i + 2] = data[i] // R <- B
    result[i + 3] = data[i + 3] // A
  }
  return result
}

// Bitmap loader using FPDFBitmap_CreateEx + FPDFImageObj_SetBitmap
const loadBitmap: ImageLoader = async (api, _doc, imageObj, blob) => {
  const { call, safeCall, writeBytes } = api

  const { data, width, height } = await ImportImage(blob)
  const bgraData = rgbaToBgra(data)
  const { pointer: dataPtr, free: freeData } = writeBytes(bgraData)

  const bitmap = await safeCall(
    E.FPDFBitmap_CreateEx,
    width,
    height,
    4, // FPDFBitmap_BGRA
    dataPtr,
    width * 4
  )

  const success = await call(
    E.FPDFImageObj_SetBitmap,
    0 as unknown as Pointer,
    0,
    imageObj,
    bitmap
  )

  if (!success) {
    await call(E.FPDFBitmap_Destroy, bitmap)
    freeData()
    throw new Error("Failed to set bitmap on image object")
  }

  return {
    cleanup: async () => {
      await call(E.FPDFBitmap_Destroy, bitmap)
      freeData()
    },
  }
}

// Unified image embedding with loader closure
async function* embedImage(
  api: PdfiumApi,
  blob: Blob,
  resolution: number,
  loader: ImageLoader
): AsyncGenerator<Blob> {
  const { call, safeCall } = api

  const { width: imgWidth, height: imgHeight } = await ImportImage(blob)
  const scale = 72 / resolution
  const pageWidth = imgWidth * scale
  const pageHeight = imgHeight * scale

  const doc = await safeCall(E.FPDF_CreateNewDocument)

  try {
    const page = await safeCall(E.FPDFPage_New, doc, 0, pageWidth, pageHeight)
    const imageObj = await safeCall(E.FPDFPageObj_NewImageObj, doc)

    const { cleanup } = await loader(api, doc, imageObj, blob)

    try {
      await call(
        E.FPDFPageObj_Transform,
        imageObj,
        pageWidth,
        0,
        0,
        pageHeight,
        0,
        0
      )

      await call(E.FPDFPage_InsertObject, page, imageObj)
      await safeCall(E.FPDFPage_GenerateContent, page)
      await call(E.FPDF_ClosePage, page)

      yield* SaveDocument(api, doc)
    } finally {
      await cleanup()
    }
  } finally {
    await call(E.FPDF_CloseDocument, doc)
  }
}

const attempts: Record<string, AttemptFunction> = {
  "image/jpeg": (api, blob, res) => embedImage(api, blob, res, loadJpeg),
  "image/png": (api, blob, res) => embedImage(api, blob, res, loadBitmap),
  "application/pdf": (api, blob) => loadPdf(api, blob),
}

function* iterateAttempts(contentType: string): Iterable<AttemptFunction> {
  if (attempts[contentType]) {
    yield attempts[contentType]
  }

  for (const [type, func] of Items(attempts)) {
    if (type !== contentType) {
      yield func
    }
  }
}

async function* ImageToPdfImpl(
  api: PdfiumApi,
  blob: Blob,
  resolution: number
): AsyncGenerator<Blob> {
  for (const attemptFn of iterateAttempts(blob.type)) {
    const { value, error } = await SafePromise(
      IterableToArray(attemptFn(api, blob, resolution))
    )

    if (error) {
      console.warn("ImageToPdf attempt failed:", error)
    } else {
      yield* value
      return
    }
  }

  throw new Error("No suitable conversion method found for image")
}

export const ImageToPdf = async (
  blob: Blob,
  image_resolution: number
): Promise<Blob> =>
  ToBlob(
    await IterableToArray(
      ProvisionPdfium(api => ImageToPdfImpl(api, blob, image_resolution))
    ),
    "application/pdf"
  )
